import { Trans, useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';

import { MplButton, MplDivider } from '@mopla/ui';

import { SubscriptionEditOption } from './ui/SubscriptionEditOption';
import { IEditConfig } from './types';

interface IDTicketSubscriptionEditContentProps {
	config: IEditConfig;
	isRevokeLoading: boolean;
}

export const DTicketSubscriptionEditContent = ({
	config,
	isRevokeLoading,
}: IDTicketSubscriptionEditContentProps) => {
	const { t } = useTranslation('dticketSubscriptionEdit');
	const { firstAction, secondAction, dates, isInvalid, hasPendingCancel } =
		config;

	/* Invalid subscriptions (subscription.status === ESubscriptionStatus.INVALID) cannot be paused,
	 * but can be cancelled.
	 *
	 * When no cancellation is scheduled:
	 * First action: PAUSE => for INVALID: should be hidden with the divider
	 * Second action: CANCEL subscription
	 *
	 * When cancellation is scheduled:
	 * First action: REVOKE cancellation => for INVALID: should be hidden (matches backend guard, TK-54)
	 * Second action: PAUSE instead of cancellation => for INVALID: should be hidden with the divider
	 */
	const shouldHideFirstAction =
		isInvalid &&
		((!hasPendingCancel && firstAction.type === 'pause') ||
			(hasPendingCancel && firstAction.type === 'revoke'));
	const shouldHideSecondAction = isInvalid && hasPendingCancel;
	const shouldShowDivider =
		!shouldHideFirstAction && !shouldHideSecondAction && !!secondAction;

	return (
		<Stack gap={3}>
			{!shouldHideFirstAction && (
				<SubscriptionEditOption
					testIdPrefix={`dticket-subscriptionEdit-${firstAction.type}`}
					title={
						<Trans
							i18nKey={`dticketSubscriptionEdit:${firstAction.titleKey}`}
							values={dates}
						/>
					}
					description={
						<Trans
							i18nKey={`dticketSubscriptionEdit:${firstAction.descriptionKey}`}
							values={dates}
							components={{ bold: <strong /> }}
						/>
					}
					warningMessage={
						firstAction.warningKey ? (
							<Trans
								i18nKey={`dticketSubscriptionEdit:${firstAction.warningKey}`}
								values={dates}
								components={{ bold: <strong /> }}
							/>
						) : undefined
					}
				>
					<MplButton
						fullWidth
						variant={firstAction.buttonVariant}
						color={firstAction.buttonColor}
						onClick={firstAction.onClick}
						disabled={firstAction.type === 'revoke' && isRevokeLoading}
						showLoader={firstAction.type === 'revoke' && isRevokeLoading}
						testId={`dticket-subscription-edit-${firstAction.type}-button`}
					>
						{t(firstAction.buttonKey)}
					</MplButton>
				</SubscriptionEditOption>
			)}

			{shouldShowDivider && <MplDivider message={t('divider_text')} />}

			{secondAction && !shouldHideSecondAction && (
				<SubscriptionEditOption
					testIdPrefix={`dticket-subscriptionEdit-${secondAction.type}`}
					title={
						<Trans
							i18nKey={`dticketSubscriptionEdit:${secondAction.titleKey}`}
							values={dates}
						/>
					}
					description={
						<Trans
							i18nKey={`dticketSubscriptionEdit:${secondAction.descriptionKey}`}
							values={dates}
							components={{ bold: <strong /> }}
						/>
					}
					warningMessage={
						secondAction.warningKey ? (
							<Trans
								i18nKey={`dticketSubscriptionEdit:${secondAction.warningKey}`}
								values={dates}
								components={{ bold: <strong /> }}
							/>
						) : undefined
					}
					infoMessage={
						secondAction.infoKey ? (
							<Trans
								i18nKey={`dticketSubscriptionEdit:${secondAction.infoKey}`}
								values={dates}
								components={{ bold: <strong /> }}
							/>
						) : undefined
					}
				>
					<MplButton
						fullWidth
						variant={secondAction.buttonVariant}
						color={secondAction.buttonColor}
						onClick={secondAction.onClick}
						testId={`dticket-subscription-edit-${secondAction.type}-button`}
					>
						{t(secondAction.buttonKey)}
					</MplButton>
				</SubscriptionEditOption>
			)}
		</Stack>
	);
};
